import React, { createContext, useContext, useEffect, useState } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    // Check for saved theme preference or default to 'dark'
    const savedTheme = localStorage.getItem('theme');
    return savedTheme || 'dark';
  });

  useEffect(() => {
    // Apply theme to document root
    const root = document.documentElement;
    
    if (theme === 'dark') {
      root.classList.add('dark');
      
      // Update CSS custom properties for dark theme with improved contrast
      root.style.setProperty('--color-background', '#0f172a'); // slate-900
      root.style.setProperty('--color-foreground', '#f8fafc'); // slate-50 (lighter for better contrast)
      root.style.setProperty('--color-border', '#334155'); // slate-700
      root.style.setProperty('--color-input', '#1e293b'); // slate-800
      root.style.setProperty('--color-card', '#1e293b'); // slate-800
      root.style.setProperty('--color-card-foreground', '#f8fafc'); // slate-50 (lighter for better contrast)
      root.style.setProperty('--color-popover', '#1e293b'); // slate-800
      root.style.setProperty('--color-popover-foreground', '#f8fafc'); // slate-50 (lighter for better contrast)
      root.style.setProperty('--color-muted', '#1e293b'); // slate-800
      root.style.setProperty('--color-muted-foreground', '#cbd5e1'); // slate-300 (lighter for better contrast)
      root.style.setProperty('--color-text-primary', '#f8fafc'); // slate-50 (lighter for better contrast)
      root.style.setProperty('--color-text-secondary', '#e2e8f0'); // slate-200 (lighter for better contrast)
      root.style.setProperty('--color-text-muted', '#cbd5e1'); // slate-300 (lighter for better contrast)
      root.style.setProperty('--color-surface', '#1e293b'); // slate-800
      root.style.setProperty('--color-surface-hover', '#334155'); // slate-700
      root.style.setProperty('--color-hover', '#334155'); // slate-700
      root.style.setProperty('--color-active', '#475569'); // slate-600
    } else {
      root.classList.remove('dark');
      
      // Reset to light theme values
      root.style.setProperty('--color-background', '#ffffff');
      root.style.setProperty('--color-foreground', '#0f172a');
      root.style.setProperty('--color-border', '#e2e8f0');
      root.style.setProperty('--color-input', '#ffffff');
      root.style.setProperty('--color-card', '#ffffff');
      root.style.setProperty('--color-card-foreground', '#0f172a');
      root.style.setProperty('--color-popover', '#ffffff');
      root.style.setProperty('--color-popover-foreground', '#0f172a');
      root.style.setProperty('--color-muted', '#f8fafc');
      root.style.setProperty('--color-muted-foreground', '#64748b');
      root.style.setProperty('--color-text-primary', '#0f172a');
      root.style.setProperty('--color-text-secondary', '#475569');
      root.style.setProperty('--color-text-muted', '#64748b');
      root.style.setProperty('--color-surface', '#f8fafc');
      root.style.setProperty('--color-surface-hover', '#f1f5f9');
      root.style.setProperty('--color-hover', '#f1f5f9');
      root.style.setProperty('--color-active', '#e2e8f0');
    }
    
    // Save theme preference
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prevTheme => prevTheme === 'light' ? 'dark' : 'light');
  };

  const value = {
    theme,
    toggleTheme,
    isDark: theme === 'dark',
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};